import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../styles/UserProfilePage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUserPlus,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import WorkflowCard from "../components/WorkflowCard";
import { formatDate } from "../helpers";
import UserLink from "../components/UserLink";
import { AuthContext } from "../AuthProvider";
import {
  getWorkflowsByUserId,
  getFollowersByUserId,
  getFollowingByUserId,
  getUserById,
  getUsersComments,
  followUser,
  unfollowUser,
  getUserFollowing,
} from "../api/api";
import { OrbitProgress } from "react-loading-indicators";

const UserProfile = () => {
  const navigate = useNavigate();
  const { keycloak, authenticated } = useContext(AuthContext);
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [workflows, setWorkflows] = useState(null);
  const [posts, setPosts] = useState(null);
  const [followers, setFollowers] = useState(null);
  const [following, setFollowing] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // "followers" or "following"
  const [followingStatus, setFollowingStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user profile first
        const usr = await getUserById(id);
        setUser(usr);

        // Only fetch other data if user was found
        const workflowz = await getWorkflowsByUserId(id);
        setWorkflows(workflowz.files);
        const followerz = await getFollowersByUserId(id);
        setFollowers(followerz);
        const followingz = await getFollowingByUserId(id);
        setFollowing(followingz);
        const commentz = await getUsersComments(id);
        setPosts(commentz);

        if (authenticated) {
          const followingUsers = await getUserFollowing(keycloak.token);
          if (
            Array.isArray(followingUsers.following) &&
            followingUsers.following.includes(id)
          ) {
            setIsFollowing(true);
          }
          const newFollowingStatus = {};
          followingUsers.following.forEach((userId) => {
            newFollowingStatus[userId] = true;
          });
          setFollowingStatus(newFollowingStatus);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        if (error.response && error.response.status === 404) {
          setError("User not found. This user may have deleted their account.");
        } else {
          setError("An error occurred while loading this profile.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchFollowing = async () => {
      const followingUsers = await getUserFollowing(keycloak.token);
      if (
        Array.isArray(followingUsers.following) &&
        followingUsers.following.includes(id)
      ) {
        setIsFollowing(true);
      }
      const newFollowingStatus = {};
      followingUsers.following.forEach((userId) => {
        newFollowingStatus[userId] = true;
      });
      setFollowingStatus(newFollowingStatus);
    };

    if (authenticated) {
      fetchFollowing();
    }
  }, [authenticated]);

  useEffect(() => {
    const fetchFollowers = async () => {
      const followerz = await getFollowersByUserId(id);
      setFollowers(followerz);
    };
    fetchFollowers();
  }, [isFollowing]);

  useEffect(() => {
    const fetchFollowing = async () => {
      const followingz = await getFollowingByUserId(id);
      setFollowing(followingz);
    };

    if (authenticated) {
      fetchFollowing();
    }
  }, [followingStatus]);

  const toggleFollow = async () => {
    if (authenticated) {
      if (isFollowing) {
        let resp = await unfollowUser(id, keycloak.token);
        if (resp) {
          setIsFollowing(false);
        }
      } else {
        let resp = await followUser(id, keycloak.token);
        if (resp) {
          setIsFollowing(true);
        }
      }
    } else {
      keycloak.login();
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const toggleFollowUser = async (id) => {
    if (authenticated) {
      if (followingStatus[id] === true) {
        let resp = await unfollowUser(id, keycloak.token);
        if (resp) {
          setFollowingStatus((prevStatus) => ({
            ...prevStatus,
            [id]: false,
          }));
        }
      } else {
        let resp = await followUser(id, keycloak.token);
        if (resp) {
          setFollowingStatus((prevStatus) => ({
            ...prevStatus,
            [id]: true,
          }));
        }
      }
    } else {
      keycloak.login();
    }
  };

  const handlePostClick = (discussionId, workflowId) => {
    setShowModal(false);
    navigate(`/workflow/${workflowId}/community/${discussionId}`);
  };

  if (loading) {
    return (
      <div className={styles.userProfilePageLoading}>
        <OrbitProgress
          color="#cb95fa"
          size="large"
          text="Loading"
          textColor=""
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className={styles.errorIcon}
          size="3x"
        />
        <h2>User Not Found</h2>
        <p>{error}</p>
        <button className={styles.backButton} onClick={() => navigate("/")}>
          Back to Home
        </button>
      </div>
    );
  }

  if (!user || !workflows || !posts || !followers || !following) {
    return (
      <div className={styles.userProfilePageLoading}>
        <OrbitProgress
          color="#cb95fa"
          size="large"
          text="Loading"
          textColor=""
        />
      </div>
    );
  }

  return (
    <div className={styles.profileContainer}>
      {/* Left Column: User Info & Follow */}
      <div className={styles.leftColumn}>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {user.picture ? (
              <img
                src={user.picture}
                alt="User Avatar"
                className={styles.avatarImage}
              />
            ) : (
              <>
                {user.first_name && user.first_name.charAt(0)}
                {user.last_name && user.last_name.charAt(0)}
                {!user.first_name && !user.last_name && "U"}
              </>
            )}
          </div>
          <h2>
            {user.first_name} {user.last_name}
          </h2>
          {user.username && (
            <p className={styles.username}>{user.username}</p>
          )}
          <p>{user.organization}</p>
          <div className={styles.stats}>
            <span
              onClick={() => openModal("followers")}
              className={styles.clickable}
            >
              <FontAwesomeIcon icon={faUsers} /> {followers.length} Followers
            </span>
            <span
              onClick={() => openModal("following")}
              className={styles.clickable}
            >
              <FontAwesomeIcon icon={faUsers} /> {following.length} Following
            </span>
          </div>
          {user.id !== keycloak.subject && (
            <button
              className={`${styles.followButton} ${
                isFollowing ? styles.unfollow : ""
              }`}
              onClick={toggleFollow}
            >
              <FontAwesomeIcon icon={faUserPlus} />{" "}
              {isFollowing ? "Unfollow" : "Follow"}
            </button>
          )}
        </div>
      </div>

      {/* Right Column: Workflows & Recent Posts */}
      <div className={styles.rightColumn}>
        {/* Workflows Section */}
        <h3>Workflows</h3>
        {workflows.length > 0 ? (
          <div className={styles.workflowsGrid}>
            {workflows.map((workflow) => (
              <WorkflowCard key={workflow.id} workflow={workflow} />
            ))}
          </div>
        ) : (
          <p>No workflows uploaded.</p>
        )}

        {/* Recent Posts Section */}
        <h3>Recent Posts</h3>
        {posts.length > 0 ? (
          <ul className={styles.postList}>
            {posts.map((post) => (
              <li key={post.id}>
                <button
                  className={styles.postLink}
                  onClick={() =>
                    handlePostClick(post.discussion_id, post.file_id)
                  }
                >
                  {post.content}
                </button>
                <span className={styles.date}>
                  {formatDate(post.updated_at)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p>No recent posts.</p>
        )}
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {modalType === "followers" ? "Followers" : "Following"}
              </h3>
              <button className={styles.closeIcon} onClick={closeModal}>
                ×
              </button>
            </div>
            <ul className={styles.modalList}>
              {(modalType === "followers" ? followers : following).map(
                (person) => (
                  <li key={person.id} className={styles.modalListItem}>
                    <div className={styles.userInfoRow}>
                      <span>
                        <UserLink
                          className={styles.userLink}
                          userId={person.id}
                          userName={person.first_name + " " + person.last_name}
                          onClick={closeModal}
                        />
                      </span>
                      {person.id !== keycloak.subject && (
                        <button
                          className={`${styles.listFollowButton} ${
                            followingStatus[person.id] ? styles.unfollow : ""
                          }`}
                          onClick={() => toggleFollowUser(person.id)}
                        >
                          {followingStatus[person.id] ? "Unfollow" : "Follow"}
                        </button>
                      )}
                    </div>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
